<script setup lang="ts">
import { type Component, onMounted, onUnmounted, reactive } from 'vue';
import {
    KeyboardIcon,
    LockIcon,
    PaintbrushIcon,
    SettingsIcon,
    VariableIcon,
} from 'lucide-vue-next';
import Dialog from '../ui/dialog/Dialog.vue';
import DialogContent from '../ui/dialog/DialogContent.vue';
import DialogDescription from '../ui/dialog/DialogDescription.vue';
import DialogTitle from '../ui/dialog/DialogTitle.vue';
import SidebarProvider from '../ui/sidebar/SidebarProvider.vue';
import Sidebar from '../ui/sidebar/Sidebar.vue';
import SidebarContent from '../ui/sidebar/SidebarContent.vue';
import SidebarGroup from '../ui/sidebar/SidebarGroup.vue';
import SidebarGroupContent from '../ui/sidebar/SidebarGroupContent.vue';
import SidebarMenu from '../ui/sidebar/SidebarMenu.vue';
import SidebarMenuButton from '../ui/sidebar/SidebarMenuButton.vue';
import SidebarMenuItem from '../ui/sidebar/SidebarMenuItem.vue';
import Breadcrumb from '../ui/breadcrumb/Breadcrumb.vue';
import BreadcrumbList from '../ui/breadcrumb/BreadcrumbList.vue';
import BreadcrumbItem from '../ui/breadcrumb/BreadcrumbItem.vue';
import BreadcrumbLink from '../ui/breadcrumb/BreadcrumbLink.vue';
import BreadcrumbSeparator from '../ui/breadcrumb/BreadcrumbSeparator.vue';
import BreadcrumbPage from '../ui/breadcrumb/BreadcrumbPage.vue';
import { eventEmitter, type SetSettingsDialogVisibilityPayload } from '@/lib/eventEmitter.ts';
import HotkeysSettingsPanel from '@/components/settings/panels/HotkeysSettingsPanel.vue';
import AppearanceSettingsPanel from '@/components/settings/panels/AppearanceSettingsPanel.vue';
import PrivacyAndVisibilitySettingsPanel from '@/components/settings/panels/PrivacyAndVisibilitySettingsPanel.vue';
import AdvancedSettingsPanel from '@/components/settings/panels/AdvancedSettingsPanel.vue';
import VariableSettingsPanel from '@/components/settings/panels/VariableSettingsPanel.vue';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface ComponentState {
    isVisible: boolean;
    activePanel: string;
}

const state: ComponentState = reactive({
    isVisible: false,
    activePanel: 'variables',
});

interface SettingPanel {
    key: string;
    name: string;
    icon: Component;
    component: Component;
}

const settingPanels: SettingPanel[] = [
    {
        key: 'variables',
        name: 'Variables',
        icon: VariableIcon,
        component: VariableSettingsPanel,
    },
    { key: 'hotkeys', name: 'Hotkeys', icon: KeyboardIcon, component: HotkeysSettingsPanel },
    {
        key: 'appearance',
        name: 'Appearance',
        icon: PaintbrushIcon,
        component: AppearanceSettingsPanel,
    },
    {
        key: 'privacyAndVisibility',
        name: 'Privacy & visibility',
        icon: LockIcon,
        component: PrivacyAndVisibilitySettingsPanel,
    },
    { key: 'advanced', name: 'Advanced', icon: SettingsIcon, component: AdvancedSettingsPanel },
];

const setActivePanel = (panelKey: string) => {
    state.activePanel = panelKey;
};

const getActivePanel = (): SettingPanel => {
    const panel = settingPanels.find((sp) => sp.key === state.activePanel);
    if (!panel) {
        throw new Error(`Unable to find settings panel with key: ${state.activePanel}`);
    }

    return panel;
};

const setDialogVisibility = (payload: SetSettingsDialogVisibilityPayload) => {
    state.isVisible = payload.visible;
};

onMounted(() => {
    eventEmitter.addListener('setSettingsDialogVisibility', setDialogVisibility);
});

onUnmounted(() => {
    eventEmitter.addListener('setSettingsDialogVisibility', setDialogVisibility);
});
</script>

<template>
    <Dialog v-model:open="state.isVisible">
        <DialogContent
            class="max-h-[500px] overflow-hidden rounded p-0 md:max-w-[700px] lg:max-w-[800px]"
        >
            <DialogTitle class="sr-only">Settings</DialogTitle>
            <DialogDescription class="sr-only"> Customize your settings here. </DialogDescription>

            <SidebarProvider class="items-start">
                <Sidebar collapsible="none" class="max-w-[50px] md:max-w-[170px]">
                    <SidebarContent>
                        <SidebarGroup>
                            <SidebarGroupContent>
                                <SidebarMenu>
                                    <!-- Loop over the nav items -->
                                    <SidebarMenuItem v-for="item in settingPanels" :key="item.name">
                                        <SidebarMenuButton
                                            as-child
                                            :isActive="item.key === state.activePanel"
                                            @click="setActivePanel(item.key)"
                                            class="flex md:hidden"
                                        >
                                            <a href="javascript:void(0)">
                                                <TooltipProvider :delay-duration="50">
                                                    <Tooltip>
                                                        <TooltipTrigger as-child>
                                                            <component :is="item.icon" />
                                                            <span>
                                                                {{ item.name }}
                                                            </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent
                                                            side="right"
                                                            :side-offset="12"
                                                        >
                                                            <p>{{ item.name }}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            </a>
                                        </SidebarMenuButton>
                                        <SidebarMenuButton
                                            as-child
                                            :isActive="item.key === state.activePanel"
                                            @click="setActivePanel(item.key)"
                                            class="hidden md:flex"
                                        >
                                            <a href="javascript:void(0)">
                                                <component :is="item.icon" />
                                                <span>
                                                    {{ item.name }}
                                                </span>
                                            </a>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </SidebarGroup>
                    </SidebarContent>
                </Sidebar>

                <main class="flex h-[500px] flex-1 flex-col overflow-hidden">
                    <header
                        class="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12"
                    >
                        <div class="flex items-center gap-2 px-4">
                            <Breadcrumb>
                                <BreadcrumbList>
                                    <BreadcrumbItem class="hidden md:block">
                                        <BreadcrumbLink href="#">Settings</BreadcrumbLink>
                                    </BreadcrumbItem>
                                    <BreadcrumbSeparator class="hidden md:block" />
                                    <BreadcrumbItem>
                                        <BreadcrumbPage>{{ getActivePanel().name }}</BreadcrumbPage>
                                    </BreadcrumbItem>
                                </BreadcrumbList>
                            </Breadcrumb>
                        </div>
                    </header>
                    <div class="flex flex-1 flex-col overflow-y-auto pt-0">
                        <component :is="getActivePanel().component"></component>
                    </div>
                </main>
            </SidebarProvider>
        </DialogContent>
    </Dialog>
</template>
